import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Bell, Sun, Moon, Search, ChevronDown } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

interface Props { title: string; }

const PortalHeader: React.FC<Props> = ({ title }) => {
  const { theme, toggleTheme, toggleSidebar, user, notifications, markAllRead, logout } = useAppStore();
  const unread = notifications.filter(n => !n.read).length;
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header style={{
      height: '4rem', background: 'var(--header-bg)', backdropFilter: 'blur(12px)',
      borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center',
      padding: '0 1.5rem', gap: '1rem', position: 'sticky', top: 0, zIndex: 30,
    }}>
      <button onClick={toggleSidebar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0.375rem', display: 'flex', alignItems: 'center', borderRadius: '0.5rem' }}>
        <Menu size={20} />
      </button>

      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h1>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} className="header-search">
        <Search size={15} style={{ position: 'absolute', left: '0.75rem', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input placeholder="Search..." style={{
          paddingLeft: '2.25rem', paddingRight: '0.75rem', paddingTop: '0.5rem', paddingBottom: '0.5rem',
          borderRadius: '0.625rem', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
          color: 'var(--text-primary)', fontSize: '0.8rem', outline: 'none', width: '200px',
        }} />
      </div>

      {/* Theme Toggle */}
      <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0.5rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center' }}>
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Notifications */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0.5rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', position: 'relative' }}>
          <Bell size={18} />
          {unread > 0 && (
            <span style={{ position: 'absolute', top: '4px', right: '4px', background: '#ef4444', color: 'white', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: 700, padding: '1px 4px', minWidth: '14px', textAlign: 'center' }}>
              {unread}
            </span>
          )}
        </button>

        {notifOpen && (
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: '340px',
            background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '0.875rem',
            boxShadow: '0 20px 40px -8px rgba(0,0,0,0.2)', zIndex: 100, overflow: 'hidden',
          }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Notifications</span>
              {unread > 0 && <button onClick={markAllRead} style={{ fontSize: '0.75rem', color: '#f97316', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
            </div>
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {notifications.slice(0, 5).map(n => (
                <div key={n.id} style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border-color)', background: n.read ? 'transparent' : 'rgb(249 115 22 / 0.03)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: n.read ? 'transparent' : '#f97316', marginTop: '6px', flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{n.title}</p>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>{n.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '0.75rem', textAlign: 'center' }}>
              <button onClick={() => setNotifOpen(false)} style={{ fontSize: '0.8rem', color: '#f97316', background: 'none', border: 'none', cursor: 'pointer' }}>View all notifications</button>
            </div>
          </div>
        )}
      </div>

      {/* Profile */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}>
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name} style={{ width: '2rem', height: '2rem', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'linear-gradient(135deg, #f97316, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '0.8rem' }}>
              {user?.name?.charAt(0) ?? 'U'}
            </div>
          )}
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} className="header-username">
            {user?.name}
          </span>
          <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
        </button>

        {profileOpen && (
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: '200px',
            background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '0.75rem',
            boxShadow: '0 10px 30px -5px rgba(0,0,0,0.15)', zIndex: 100, overflow: 'hidden', padding: '0.5rem',
          }}>
            {[
              { label: 'My Profile', action: () => navigate('/portal/profile') },
              { label: 'Settings', action: () => navigate('/admin/settings') },
              { label: 'Sign Out', action: () => { logout(); navigate('/login'); }, danger: true },
            ].map(item => (
              <button key={item.label} onClick={() => { item.action(); setProfileOpen(false); }} style={{
                width: '100%', textAlign: 'left', padding: '0.625rem 0.875rem', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: '0.85rem', color: (item as any).danger ? '#ef4444' : 'var(--text-primary)',
                borderRadius: '0.5rem', transition: 'background 0.15s',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 640px) { .header-search { display: none; } .header-username { display: none; } }
      `}</style>
    </header>
  );
};

export default PortalHeader;
